import { AlertCircle, MessageSquare } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { AttachmentDownloadButton } from "./AttachmentDownloadButton";
import { useRequestComments } from "./useOrderSupportRequests";

interface RequestCommentsProps {
  orderId: string;
  zendeskTicketId: number;
}

/**
 * "Últimas interações" do chamado (protótipo 2b): as respostas públicas do Zendesk, da mais recente para a mais
 * antiga. A equipe aparece como "Atendimento"; notas internas nunca chegam aqui. O texto é exibido como texto puro.
 */
export const RequestComments = ({ orderId, zendeskTicketId }: RequestCommentsProps) => {
  const { data, isLoading, isError, isFetching, refetch } = useRequestComments(orderId, zendeskTicketId);
  const comments = data?.comments;

  return (
    <section className="flex flex-col gap-3" aria-labelledby="request-comments-title">
      <h3 id="request-comments-title" className="text-[16px] font-bold leading-tight">
        Últimas interações
      </h3>
      {/* Falha ao atualizar com respostas já na tela: mantém o que já havia e só avisa. */}
      {isError && comments && (
        <p role="status" className="text-[13px] text-muted-foreground">
          Não foi possível atualizar as respostas agora. Mostrando as últimas carregadas.
        </p>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-3" aria-busy="true" aria-label="Carregando respostas">
          <Skeleton className="h-16 w-full rounded-[10px]" />
          <Skeleton className="h-16 w-full rounded-[10px]" />
        </div>
      ) : isError && !comments ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="flex flex-col items-start gap-3">
            <p>Não foi possível carregar as respostas do atendimento agora.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : !comments || comments.length === 0 ? (
        <p className="flex items-center gap-2 rounded-[10px] border border-dashed px-4 py-4 text-sm text-muted-foreground">
          <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
          Ainda não há respostas do atendimento.
        </p>
      ) : (
        <ol className="flex flex-col gap-3" aria-label="Respostas do atendimento">
          {comments.map((comment) => (
            <li
              key={comment.id}
              className={cn(
                "flex flex-col gap-1.5 rounded-[10px] border px-4 py-3",
                comment.isFromSupport ? "bg-primary/5" : "bg-background"
              )}
            >
              <p className="text-[13px] text-muted-foreground">
                <span className="font-semibold text-foreground">{comment.authorName}</span>
                {comment.createdAt && <> · {formatDateTime(new Date(comment.createdAt))}</>}
              </p>
              {comment.body && (
                <p className="whitespace-pre-line break-words text-[14.5px] leading-relaxed">{comment.body}</p>
              )}
              {comment.attachments.length > 0 && (
                <ul className="mt-1 flex flex-wrap gap-2" aria-label="Anexos da resposta">
                  {comment.attachments.map((attachment) => (
                    <li key={attachment.id}>
                      <AttachmentDownloadButton
                        orderId={orderId}
                        zendeskTicketId={zendeskTicketId}
                        attachment={attachment}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
